<template>
  <teleport to="body">
    <div 
      v-if="visible && statusEffect" 
      class="status-tooltip"
      :style="{ top: `${position.y}px`, left: `${position.x}px` }">
      
      <!-- Header -->
      <div class="tooltip-header">
        <div class="status-header-content">
          <i class="material-icons status-icon" :style="{ color: effectConfig.color }">
            {{ effectConfig.icon }}
          </i>
          <h4>{{ effectConfig.name }}</h4>
        </div>
        <span class="effect-type-badge" :class="{ 'buff': !isDebuff, 'debuff': isDebuff }">
          {{ isDebuff ? 'DEBUFF' : 'BUFF' }}
        </span>
      </div>

      <!-- Effect Details -->
      <div class="tooltip-body">
        <!-- Effect Value -->
        <div class="effect-row">
          <i class="material-icons">trending_up</i>
          <span>{{ $t('statusEffects.effect') }}</span>
          <span class="value" :class="{ 'positive': !isDebuff, 'negative': isDebuff }">
            {{ formatEffectValue(statusEffect.effectValue) }}
          </span>
        </div>

        <!-- Duration -->
        <div class="effect-row">
          <i class="material-icons">schedule</i>
          <span>{{ $t('statusEffects.timeRemaining') }}</span>
          <span class="value duration" :class="{ 'expiring': isExpiringSoon(statusEffect.expiresAt) }">
            {{ formatTimeRemaining(statusEffect.expiresAt) }}
          </span>
        </div>

        <!-- Source -->
        <div v-if="statusEffect.source" class="effect-row">
          <i class="material-icons">source</i>
          <span>{{ $t('statusEffects.source') }}</span>
          <span class="value">{{ formatSource(statusEffect.source) }}</span>
        </div>

        <!-- Cure Cost (for debuffs) -->
        <div v-if="isDebuff && statusEffect.curable" class="effect-row cure-cost">
          <i class="material-icons">healing</i>
          <span>{{ $t('statusEffects.cureCost') }}</span>
          <span class="value coins">
            <i class="material-icons">monetization_on</i>
            {{ statusEffect.cureCost || 0 }}
          </span>
        </div>

        <!-- Resistance (for fighter debuffs) -->
        <div v-if="isDebuff && statusEffect.resistance" class="effect-row">
          <i class="material-icons">shield</i>
          <span>{{ $t('statusEffects.resistance') }}</span>
          <span class="value">{{ Math.round(statusEffect.resistance * 100) }}%</span>
        </div>
      </div>

      <!-- Description -->
      <div v-if="getEffectDescription()" class="tooltip-description">
        <p>{{ getEffectDescription() }}</p>
      </div>

      <!-- Progress Bar for Time -->
      <div class="time-progress">
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ 
              width: `${getTimeProgress()}%`,
              backgroundColor: isExpiringSoon(statusEffect.expiresAt) ? '#e74c3c' : effectConfig.color
            }">
          </div>
        </div>
        <div class="progress-scanline"></div>
      </div>
    </div>
  </teleport>
</template>

<script>
import statusEffectsMixin from '../mixins/statusEffectsMixin';

export default {
  name: 'StatusTooltip',
  mixins: [statusEffectsMixin],
  
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    statusEffect: {
      type: Object,
      default: null
    },
    position: {
      type: Object,
      default: () => ({ x: 0, y: 0 })
    },
    isDebuff: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    effectConfig() {
      if (!this.statusEffect) return { name: '', color: '#666666', icon: 'help' };
      
      return this.isDebuff 
        ? this.getDebuffConfig(this.statusEffect.effectType) || { name: this.statusEffect.effectType, color: '#e74c3c', icon: 'warning' }
        : this.getBuffConfig(this.statusEffect.effectType) || { name: this.statusEffect.effectType, color: '#2ecc71', icon: 'trending_up' };
    }
  },

  methods: {
    getEffectDescription() {
      const descriptions = {
        // Player buffs
        'xp_multiplier': 'Aumenta a experiência ganha em atividades',
        'coins_multiplier': 'Multiplica as moedas obtidas',
        'production_speed': 'Acelera a produção de negócios',
        'gym_training': 'Melhora a eficiência do treinamento',
        'business_income': 'Aumenta a renda dos negócios',

        // Fighter buffs
        'strength_boost': 'Aumenta temporariamente a força do lutador',
        'agility_boost': 'Melhora a agilidade e velocidade', 
        'defense_boost': 'Fortalece as defesas do lutador',
        'stamina_boost': 'Aumenta a resistência máxima',
        'attack_boost': 'Potencializa o poder de ataque',
        'dodge_boost': 'Aumenta a chance de esquiva',

        // Player debuffs
        'market_crash': 'Crash do mercado reduz ganhos de produção',
        'reputation_loss': 'Perda de reputação afeta preços de venda',
        'tax_audit': 'Auditoria fiscal reduz ganhos líquidos',
        'supply_shortage': 'Escassez aumenta custos de produção',
        'economic_recession': 'Recessão econômica impacta todos os ganhos',

        // Fighter debuffs  
        'bleeding': 'Sangramento causa perda gradual de vida',
        'poisoned': 'Veneno reduz todos os atributos do lutador',
        'weakened': 'Fraqueza diminui força e poder de ataque',
        'stunned': 'Atordoamento reduz drasticamente a agilidade',
        'fatigued': 'Fadiga limita a stamina máxima',
        'diseased': 'Doença enfraquece força e defesa',
        'injured': 'Ferimentos reduzem ataque e defesa'
      };

      return descriptions[this.statusEffect?.effectType] || '';
    },

    formatSource(source) {
      const sources = {
        'battle': 'Batalha',
        'food': 'Comida', 
        'business': 'Negócio',
        'premium': 'Item Premium',
        'event': 'Evento',
        'manual': 'Aplicado Manualmente',
        'system': 'Sistema'
      };

      return sources[source] || source;
    },

    getTimeProgress() {
      if (!this.statusEffect) return 0;
      
      const now = new Date().getTime();
      const createdAt = new Date(this.statusEffect.createdAt).getTime();
      const expiresAt = new Date(this.statusEffect.expiresAt).getTime();
      
      const totalDuration = expiresAt - createdAt;
      const remaining = Math.max(0, expiresAt - now);
      
      return Math.min(100, (remaining / totalDuration) * 100);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../assets/styles/colors.scss';

.status-tooltip {
  position: fixed;
  z-index: 9999;
  background: linear-gradient(145deg, rgba(45, 45, 45, 0.95), rgba(26, 26, 26, 0.98));
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 16px;
  min-width: 280px;
  max-width: 350px;
  backdrop-filter: blur(10px);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  font-family: 'Rajdhani', sans-serif;
  pointer-events: none;
  animation: fadeInTooltip 0.2s ease-out;

  // Cyberpunk border effect
  background-clip: padding-box;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, $primary-blue, $primary-red, $primary-green);
    border-radius: 12px;
    z-index: -1;
    opacity: 0.7;
    animation: borderGlow 2s ease-in-out infinite alternate;
  }
}

.tooltip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  .status-header-content {
    display: flex;
    align-items: center;
    gap: 8px;

    .status-icon {
      font-size: 24px;
      filter: drop-shadow(0 0 4px currentColor);
    }

    h4 {
      margin: 0;
      color: $text-primary;
      font-size: 18px;
      font-weight: 600;
      text-shadow: 0 0 8px rgba(255, 255, 255, 0.3);
    }
  }

  .effect-type-badge {
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;

    &.buff {
      background: rgba(46, 204, 113, 0.2);
      color: $success;
      border: 1px solid rgba(46, 204, 113, 0.4);
      text-shadow: 0 0 4px $success;
    }

    &.debuff {
      background: rgba(231, 76, 60, 0.2);
      color: $danger;
      border: 1px solid rgba(231, 76, 60, 0.4);
      text-shadow: 0 0 4px $danger;
    }
  }
}

.tooltip-body {
  margin-bottom: 12px;

  .effect-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    padding: 6px 0;

    .material-icons {
      font-size: 16px;
      color: $text-secondary;
      min-width: 20px;
    }

    span:nth-child(2) {
      flex: 1;
      color: $text-secondary;
      font-size: 14px;
    }

    .value {
      font-weight: 600;
      font-size: 14px;

      &.positive {
        color: $success;
        text-shadow: 0 0 4px rgba(46, 204, 113, 0.3);
      }

      &.negative {
        color: $danger;
        text-shadow: 0 0 4px rgba(231, 76, 60, 0.3);
      }

      &.duration {
        color: $primary-blue;
        
        &.expiring {
          color: $danger;
          animation: pulse 1s infinite;
        }
      }

      &.coins {
        color: $primary-yellow;
        display: flex;
        align-items: center;
        gap: 4px;

        .material-icons {
          font-size: 16px;
        }
      }
    }

    &.cure-cost {
      background: rgba(255, 255, 255, 0.05);
      padding: 8px;
      border-radius: 6px;
      margin: 8px 0;
    }
  }
}

.tooltip-description {
  margin-bottom: 12px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border-left: 3px solid $primary-blue;

  p {
    margin: 0;
    color: $text-secondary;
    font-size: 13px;
    line-height: 1.4;
    font-style: italic;
  }
}

.time-progress {
  position: relative;
  margin-top: 8px;

  .progress-bar {
    width: 100%;
    height: 4px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
    overflow: hidden;
    position: relative;

    .progress-fill {
      height: 100%;
      transition: width 0.3s ease;
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        animation: progressGlow 2s ease-in-out infinite;
      }
    }
  }

  .progress-scanline {
    position: absolute;
    top: 0;
    left: 0;
    width: 2px;
    height: 4px;
    background: rgba(255, 255, 255, 0.8);
    animation: scanline 2s linear infinite;
  }
}

// Animations
@keyframes fadeInTooltip {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes borderGlow {
  from {
    opacity: 0.5;
  }
  to {
    opacity: 0.8;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

@keyframes progressGlow {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes scanline {
  0% {
    left: 0;
  }
  100% {
    left: calc(100% - 2px);
  }
}
</style>